import { IHttpService, IPromise, IRequestShortcutConfig } from "angular";
import { encode as base64encode } from "js-base64";
import { EnvironmentService } from "@gtmhub/env";
import { IInsightboard, IInsightboardItemSetting } from "@gtmhub/insightboards/models/models";
import { IKpi } from "@gtmhub/kpis";
import { ITag } from "@gtmhub/tags/models";
import { ICollection } from "@webapp/core/core.models";
import { InsightCalculationPayload, InsightCalculationTransaction, InsightsQueryParams } from "@webapp/data-story/models/insights.models";
import { Parameter } from "@webapp/data-story/models/parameters-facade.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { IProfile } from "@webapp/user-profile/models/user-profile.models";
import {
  IDirectSubmitInsight,
  IGetMarketplaceInsightTemplatesQuery,
  IInsight,
  IInsightBlueprint,
  IInsightChangeLogs,
  IInsightEntityRequiredField,
  IInsightMetricUsageMap,
  IInsightSqlConditionOperators,
  IInsightTotalUsageCount,
  IInsightUsedEntity,
  IMarketplaceInsightTemplate,
  IMarketplaceInsightTemplateInstallRequest,
  IMarketplaceInsightTemplateInstallResponse,
  ISubmitInsightEmailContent,
  IUpdateInsight,
} from "../models/models";

export class InsightService {
  public static $inject = ["$http", "EnvironmentService"];

  constructor(private $http: IHttpService, private env: EnvironmentService) {}

  public getInsightFieldsToUpdate(newInsight: IUpdateInsight, oldInsight: IInsight): IUpdateInsight {
    const insightFieldsToUpdate: IUpdateInsight = {
      id: newInsight.id,
    };

    Object.keys(newInsight).forEach((key) => {
      if (typeof newInsight[key] !== "object") {
        if (newInsight[key] !== oldInsight[key]) {
          insightFieldsToUpdate[key] = newInsight[key];
        }
      } else {
        for (const subKey in newInsight[key]) {
          if (!oldInsight[key] || newInsight[key][subKey] !== oldInsight[key][subKey]) {
            insightFieldsToUpdate[key] = newInsight[key];
            break;
          }
        }
      }
    });

    if (insightFieldsToUpdate.algorithm) {
      insightFieldsToUpdate.algorithm = base64encode(insightFieldsToUpdate.algorithm);
    }

    if (insightFieldsToUpdate.view) {
      insightFieldsToUpdate.view = base64encode(insightFieldsToUpdate.view);
    }

    return insightFieldsToUpdate;
  }

  public patchInsight(insight: IUpdateInsight): IPromise<void> {
    const url = this.env.getApiEndpoint("/insights/" + insight.id);

    return this.$http.patch<void>(url, insight).then(() => null);
  }

  public getInsightBlueprints(categoryId?: string): IPromise<IInsightBlueprint[]> {
    let url = this.env.getApiEndpoint("/insights/blueprints");
    if (categoryId) {
      url += "?categoryId=" + categoryId;
    }

    return this.$http.get<ICollection<IInsightBlueprint>>(url).then((response) => response.data.items);
  }

  public getInsights(categoryId: string = null, insightIds: string = null, fields: string = null): IPromise<ICollection<IInsight>> {
    const url: string = this.env.getApiEndpoint("/insights");
    const query: IRequestShortcutConfig = {
      params: {
        categoryId: categoryId,
        insightIds: insightIds,
        fields: fields,
      },
    };

    return this.$http.get<ICollection<IInsight>>(url, query).then((response) => response.data);
  }

  public getInsightsV2(queryParams: InsightsQueryParams): IPromise<ICollection<IInsight>> {
    const url: string = this.env.getApiEndpointV2("/insights");
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<ICollection<IInsight>>(url, query).then((response) => response.data);
  }

  public getInsightsSafe(insightIds: string = null, fields: string = null): IPromise<ICollection<IInsight>> {
    const url: string = this.env.getApiEndpoint("/insights/safe");
    const query: IRequestShortcutConfig = {
      params: {
        insightIds: insightIds,
        fields: fields,
      },
    };

    return this.$http.get<ICollection<IInsight>>(url, query).then((response) => response.data);
  }

  public getInsight(name: string): IPromise<IInsight> {
    const url = this.env.getApiEndpoint("/insights/" + name);

    return this.$http.get<IInsight>(url).then((response) => response.data);
  }

  public getInsightV2(name: string): IPromise<IInsight> {
    const url = this.env.getApiEndpointV2("/insights/" + name);

    return this.$http.get<IInsight>(url).then((response) => response.data);
  }

  public getInsightTotalUsageCount(id: string): IPromise<IInsightTotalUsageCount> {
    const url: string = this.env.getApiEndpoint(`insights/${id}/usages/count`);
    return this.$http.get<IInsightTotalUsageCount>(url).then((response) => response.data);
  }

  public getInsightSqlConditionOperators(): IPromise<ICollection<IInsightSqlConditionOperators>> {
    const url = this.env.getApiEndpoint("/insights/query/properties");

    return this.$http.get<ICollection<IInsightSqlConditionOperators>>(url).then((response) => response.data);
  }

  public getInsightSafe(name: string): IPromise<IInsight> {
    const url: string = this.env.getApiEndpoint(`/insights/safe/${name}`);

    return this.$http.get<IInsight>(url).then((response) => response.data);
  }

  public getUsageOnBoards(id: string): IPromise<IInsightboard[]> {
    const url = this.env.getApiEndpoint("/insights/" + id + "/usages/boards");

    return this.$http.get<IInsightboard[]>(url).then((response) => response.data);
  }

  public getUsageOnKpis(id: string): IPromise<IKpi[]> {
    const url = this.env.getApiEndpoint("/insights/" + id + "/usages/kpis");
    return this.$http.get<IKpi[]>(url).then((response) => response.data);
  }

  public getUsageInMetrics(id: string): IPromise<Metric[]> {
    const url = this.env.getApiEndpoint("/insights/" + id + "/usages/metrics");

    return this.$http.get<Metric[]>(url).then((response) => response.data);
  }

  public createInsight(insight: IInsight, options: { cloned?: boolean } = {}): IPromise<IInsight> {
    const url = this.env.getApiEndpoint("/insights");

    return this.$http
      .post<IInsight>(url, insight, {
        params: options.cloned ? { gtmhubAdditionalParams: { cloned: options.cloned } } : {},
      })
      .then((response) => response.data);
  }

  public deleteInsight(name: string): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/insights/" + name);

    return this.$http.delete<boolean>(url).then((response) => response.data);
  }

  public addTag(insight: IInsight, tag: ITag): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/insights/" + insight.id + "/tags");

    return this.$http.post(url, tag).then((response) => response.data);
  }

  public removeTagFromInsight(insightId: string, tag: ITag): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/insights/" + insightId + "/tags");

    return this.$http({
      method: "DELETE",
      url: url,
      data: { title: tag.title },
      headers: {
        "Content-type": "application/json;charset=utf-8",
      },
    });
  }

  public requestInsightCalculationById(insightId: string, filters: IInsightboardItemSetting[] = null): IPromise<InsightCalculationTransaction> {
    const url: string = this.env.getApiEndpoint(`/insights/${insightId}/calculate`);

    // backend looks for filters in payload
    const payload: { filters: IInsightboardItemSetting[] } = {
      filters,
    };

    return this.$http.post<InsightCalculationTransaction>(url, payload).then((response) => response.data);
  }

  public requestInsightScriptCalculation(payload: InsightCalculationPayload): IPromise<InsightCalculationTransaction> {
    const url: string = this.env.getApiEndpoint("/insights/execute");

    return this.$http.post<InsightCalculationTransaction>(url, payload).then((response) => response.data);
  }

  public getInsightsUsagesInMetrics(insightIds: string): IPromise<IInsightMetricUsageMap> {
    const url: string = this.env.getApiEndpoint("/insights/metrics");
    const query: IRequestShortcutConfig = {
      params: {
        insightIds: insightIds,
      },
    };

    return this.$http.get<IInsightMetricUsageMap>(url, query).then((response) => response.data);
  }

  public generateSubmitInsightEmail(insight: IInsight, currentUserProfile: IProfile): ISubmitInsightEmailContent {
    const mailto = "marketplace@gtmhub.com";
    const subject = `Submit insight "${insight.title}" to Quantive marketplace`;

    const usedDataSources: string = this.generateUsedDataSourcesText(insight);

    const usedFilters: string = this.generateUsedFiltersText(insight.usedFilters);

    const body: string = this.generateEmailBody(insight, currentUserProfile, usedDataSources, usedFilters);

    return {
      body,
      mailto,
      subject,
    };
  }

  public getInsightChangeLogs(insightId: string, queryParams?: InsightsQueryParams): IPromise<IInsightChangeLogs> {
    const url: string = this.env.getApiEndpoint("/insights/" + insightId + "/changelogs");
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<IInsightChangeLogs>(url, query).then((response) => response.data);
  }

  public getMarketplaceInsightTemplates(query: IGetMarketplaceInsightTemplatesQuery): IPromise<ICollection<IMarketplaceInsightTemplate>> {
    const url = this.env.getMarketplaceEndpointV2("/templates/insights");
    return this.$http.get<ICollection<IMarketplaceInsightTemplate>>(url, { params: query }).then((res) => res.data);
  }

  public installMarketplaceInsightTemplates(templates: IMarketplaceInsightTemplateInstallRequest[]): IPromise<IMarketplaceInsightTemplateInstallResponse> {
    const url = this.env.getMarketplaceEndpointV2("/templates/insights/install");
    return this.$http.post<IMarketplaceInsightTemplateInstallResponse>(url, templates).then((res) => res.data);
  }

  private generateUsedDataSourcesText(insight: IInsight): string {
    if (insight.usedEntities) {
      return insight.usedEntities.reduce((usedSources: string, currentUsedEntity: IInsightUsedEntity): string => {
        const requiredFields: string = this.generateRequiredFieldsText(currentUsedEntity.requiredFields);

        usedSources += `
            %0D%09* Entity name: ${currentUsedEntity.name}%0D
            %09* Data source blueprint name: ${currentUsedEntity.dataSourceBlueprintName}%0D
            %09* Remote entity name: ${currentUsedEntity.remoteEntityName}%0D
            %09* Required fields: ${requiredFields}%0D
          `;

        return usedSources;
      }, "");
    }

    return "-";
  }

  private generateRequiredFieldsText(fields: IInsightEntityRequiredField[]): string {
    if (Array.isArray(fields) && fields.length > 0) {
      return fields.reduce((reqFields: string, currentReqField: IInsightEntityRequiredField): string => {
        reqFields += `${currentReqField.name}; `;

        return reqFields;
      }, "");
    }

    return "-";
  }

  private generateUsedFiltersText(filters: string[]): string {
    if (Array.isArray(filters) && filters.length > 0) {
      return filters.reduce((filtersUsed: string, currentFilterName: string): string => {
        filtersUsed += `${currentFilterName}; `;

        return filtersUsed;
      }, "");
    }

    return "-";
  }

  private generateEmailBody(insight: IInsight, currentUserProfile: IProfile, usedDataSources: string, usedFilters: string): string {
    return `
        Title: ${insight.title}%0D
        Description: ${insight.description}%0D%0D
        Algorithm: %0D%09${insight.algorithm.replace(/\n/gi, "%0D%09")}%0D%0D
        View: %0D%09${insight.view.replace(/\n/gi, "%0D%09")}%0D%0D
        Used data sources: ${usedDataSources}%0D
        Filters used: ${usedFilters}%0D%0D
        Formatting: editor: ${insight.formatting?.editor ? insight.formatting.editor : "-"}%0D%0D
        Created by: ${insight.createdBy ? insight.createdBy.name : "Unknown author"}%0D
        Submitted by: ${currentUserProfile.firstName} ${currentUserProfile.lastName}%0D
      `;
  }

  public submitInsightToMarketplace(insightData: IDirectSubmitInsight): IPromise<IInsight> {
    const url: string = this.env.getMarketplaceEndpointV2("/templates/insights");

    return this.$http.post<IInsight>(url, insightData).then((response) => response.data);
  }

  public getInsightUsedFilters = (insight: IInsight, parameters: Parameter[]): Parameter[] => {
    return parameters.filter((parameter) => insight.usedFilters.indexOf(parameter.key) !== -1);
  };

  public duplicateInsight(insightName: string): IPromise<IInsight> {
    return this.getInsight(insightName).then((insight) => {
      const newInsight = { ...insight, title: `${insight.title} Cloning` };
      delete newInsight.id;
      delete newInsight.name;

      return this.createInsight(newInsight).then((clonedInsight) => clonedInsight);
    });
  }
}
